import { createContext, useContext, useEffect, useRef, useState } from "react";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import ReportExplorer from "@/appComponents/Blocks/Modals/ReportExplorer/ReportExplorer";
import { DoReportRFC } from "@/Utils/Promises";
import { createMinimumDelay } from "@/Utils/Utils";
import { kDefaultDirFields } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { appstr } from "@coreapp/utils/appstr";

const ReportingContext = createContext();

const GetDefaultSearchState = () => {
  return {
    columnNames: kDefaultDirFields,
    loading: false,
    results: []
  };
};

const GenerateReportStructure = (chartID = null) => {
  return {
    name: null,
    chartID: chartID,
    description: "",
    filters: [],
    columnNames: kDefaultDirFields
  };
};

const ReportsProvider = ({ children }) => {
  const nav = useAppNavigate();
  const [reportSearchState, setReportSearchState] = useState(GetDefaultSearchState());
  const [isOpenReportDirty, setOpenReportDirty] = useState(false);
  const [currentOpenReport, setCurrentOpenReport] = useState(null);
  const [showReportExplorer, setShowReportExplorer] = useState(false);
  const [marshalledFilters, setMarshalledFilters] = useState(undefined);
  const [isSaving, setSaving] = useState(false);
  const [isSearching, setSearching] = useState(false);
  const [reports, setReports] = useState([]);
  const searchCtrl = useRef(null);
  const tableHandleRef = useRef(null);
  const usubscribeToFilterChanges = useRef(null);

  const LoadReports = async () => {
    const { result } = await DoReportRFC("loadreports");
    setReports(result["reports"] ?? []);
  };

  useEffect(() => {
    if (!showReportExplorer) {
      return;
    }
    LoadReports();
  }, [showReportExplorer]);

  const BroadcastReportId = reportId => {
    const filterMgr = GetDoc().GetFilterMgr();
    filterMgr.SetReportId(reportId);
  };

  const CreateReport = async () => {
    BroadcastReportId("new");
    const doc = GetDoc();
    const filterMgr = doc.GetFilterMgr();
    const baseChartID = doc.GetChartID();
    filterMgr.SetFilterBlocks("reporting_new", []);
    setOpenReportDirty(true);
    setCurrentOpenReport(GenerateReportStructure(baseChartID));
    setReportSearchState(GetDefaultSearchState());
    OpenReportMode();
  };

  const OpenReportMode = () => {
    closeReportExplorer();
    nav.setMode("reports");
    SubscribeToFilterChanges();
  };

  const OpenReport = async reportObj => {
    if (reportObj["ID"] == null) return;

    BroadcastReportId(reportObj["ID"]);
    const data = { reportID: reportObj["ID"] };
    const { result } = await DoReportRFC("openreport", data);

    if (result["success"]) {
      const flatObj = result["report"];
      const reportObj = UnflattenReport(flatObj);
      LoadFilters(reportObj);
      setOpenReportDirty(false);
      setCurrentOpenReport(reportObj);
      setReportSearchState({ ...GetDefaultSearchState(), columnNames: reportObj["columnNames"] });
      Globals._svrChartMgr.SetLastReportID(flatObj["ID"]);
      OpenReportMode();
    }
  };

  const FlattenReport = reportObj => {
    const flatObj = structuredClone(reportObj);
    flatObj["filters"] = reportObj["filters"].map(f => {
      const data = f.OutputToJSON();
      delete data.contextId;
      return data;
    });
    flatObj["columnNames"] = reportSearchState["columnNames"];
    return flatObj;
  };

  const UnflattenReport = flatObj => {
    const contextId = `reporting_${flatObj["ID"]}`;
    const filterBlocks = (flatObj["filters"] ?? []).map(flatFilter => {
      const filterBlock = new QFilterBlock();
      filterBlock.InitFilterBlock();
      filterBlock.LoadFromJSON({ ...flatFilter, contextId });
      return filterBlock;
    });

    return {
      ...flatObj,
      filters: filterBlocks
    };
  };

  const LoadFilters = reportObj => {
    const doc = GetDoc();
    const filterMgr = doc.GetFilterMgr();
    const contextId = `reporting_${reportObj["ID"]}`;
    filterMgr.SetFilterBlocks(contextId, reportObj["filters"]);

    // This call is necessary because the context ID of the manager can not be the
    // same context of the report that is being opened.
    // By setting it manually here, we ensure the first search will include the report's filters
    UpdateMarshalledFilters(reportObj["filters"]);
  };

  const UpdateMarshalledFilters = filterBlocks => {
    const marshalledFilters = filterBlocks.map(f => f.Marshall()).flat();

    setMarshalledFilters(marshalledFilters);
  };

  const UpdateOpenReport = (updateDict, markDirty = true) => {
    if (markDirty) {
      setOpenReportDirty(true);
    }

    setCurrentOpenReport(prevState => {
      return { ...prevState, ...updateDict };
    });
  };

  const SaveReportChanges = async ({ additionalData } = {}) => {
    const delay = createMinimumDelay(300);
    try {
      setSaving(true);
      let response = {};

      if (!currentOpenReport["ID"]) {
        const flatObj = FlattenReport({
          ...currentOpenReport,
          chartID: GetDoc()?.GetChartID(),
          ...additionalData
        });
        if (!flatObj?.name) {
          const namesList = reports.map(report => {
            return report.name;
          });
          flatObj.name = namesList.GetUniqueDuplicateName(appstr("Untitled Report"), "", true);
        }

        const data = { newReportData: flatObj };
        response = await DoReportRFC("savereport", data);
      } else {
        const flatObj = FlattenReport({ ...currentOpenReport, ...additionalData });
        const data = { updateData: flatObj, reportID: flatObj["ID"] };
        response = await DoReportRFC("updatereport", data);
      }

      if (response?.result["success"]) {
        const flatObj = response.result["report"];
        const reportObj = UnflattenReport(flatObj);
        LoadFilters(reportObj);
        setCurrentOpenReport(reportObj);
        setOpenReportDirty(false);
        Globals._svrChartMgr.SetLastReportID(flatObj["ID"]);
      }
    } catch (e) {
      const code = e?.result?.code;
      if (code === 5004) {
        const alert = new QAlert();
        alert.InitAlert(appstr("Rename failure"), appstr("nameinuse"), null, Globals._wsMgr);
      }
    } finally {
      await delay();
      setSaving(false);
    }
  };

  const PrepEnterReporting = () => {
    const wsMgr = GetWSMgr();
    if (Globals._svrChartMgr.GetLastReportID() != null && !isInReporting()) {
      var headerText = appstr("Resume last open report");
      var yesStr = appstr("Continue Reporting");
      var noStr = appstr("Report explorer");
      var infoText = appstr("Do you want to resume work in your last open report?");
      wsMgr.CreateReactAlert(
        headerText,
        infoText,
        () => OpenReport({ ID: Globals._svrChartMgr.GetLastReportID() }),
        () => openReportExplorer(),
        yesStr,
        noStr
      );
    } else {
      openReportExplorer();
    }
  };

  const SubscribeToFilterChanges = () => {
    usubscribeToFilterChanges.current?.();

    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    const filterMgr = doc.GetFilterMgr();
    const contextId = filterMgr.GetContextId();
    usubscribeToFilterChanges.current = wsMgr?.SubscribeToTopic(
      `FilterUpdate_${contextId}`,
      filters => {
        setOpenReportDirty(true);
        setCurrentOpenReport(p => ({ ...p, filters }));
        UpdateMarshalledFilters(filters);
      }
    );
  };

  const openReportExplorer = () => {
    const wsMgr = GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("reporting.report_explorer.opened");
    setShowReportExplorer(true);
  };

  const closeReportExplorer = () => {
    setShowReportExplorer(false);
  };

  const handleSetReportSearchState = stateUpdater => {
    setOpenReportDirty(true);
    setReportSearchState(stateUpdater);
  };

  const UpdateSearchResults = newResults => {
    setReportSearchState(prevState => {
      return { ...prevState, results: newResults.slice(0) };
    });
  };

  const DoSearch = async () => {
    const delay = createMinimumDelay(300);
    try {
      setSearching(true);
      await searchCtrl.current?.DoSearch();
    } finally {
      await delay();
      setSearching(false);
    }
  };

  let contextValue = {
    isOpenReportDirty,
    PrepEnterReporting,
    UpdateOpenReport,
    OpenReport,
    CreateReport,
    openReportExplorer,
    closeReportExplorer,
    SaveReportChanges,
    currentOpenReport,
    tableHandleRef,
    reportSearchState,
    setReportSearchState: handleSetReportSearchState,
    showReportExplorer,
    UpdateSearchResults,
    marshalledFilters,
    isSaving,
    searchCtrl,
    isSearching,
    DoSearch,
    reports,
    setReports
  };

  return (
    <ReportingContext.Provider value={contextValue}>
      {children}
      {showReportExplorer && <ReportExplorer />}
    </ReportingContext.Provider>
  );
};

export const useReportingContext = () => useContext(ReportingContext);

export default ReportsProvider;
